import { AlertTriangle, Calendar, ChevronDown, ChevronsLeft, ChevronsRight, Columns3, List, LogOut, Menu, RefreshCw, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { copy, itemBadge, navLabel } from "../copy";
import { topicAccount } from "../domain/accounts";
import { statusCounts, topicsForAccount } from "../domain/selectors";
import { tasksForAccount } from "../domain/tasks";
import { todayInShanghai } from "../domain/time";
import { decideTopics, dueToday, reviewTopics } from "../domain/worklist";
import { reviewShot } from "../lib/shot";
import { HOME_ITEM, NAV_GROUPS, type NavGroupId, type RouteId } from "../routes";
import { useApp } from "../state/store";
import { AddAccount } from "./AddAccount";
import { ErrorBoundary } from "./ErrorBoundary";
import { WorkspaceMenu } from "./WorkspaceMenu";
import { DetailModal } from "./DetailModal";
import { Home } from "../views/Home";
import { Inbox } from "../views/Inbox";
import { KanbanDesk } from "../views/KanbanDesk";
import { Metrics } from "../views/Metrics";
import { Research } from "../views/Research";
import { ReviewDesk } from "../views/ReviewDesk";
import { Settings as SettingsView } from "../views/Settings";

export function Shell() {
  const app = useApp();
  const [openGroups, setOpenGroups] = useState<Record<NavGroupId, boolean>>(readNavGroups);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    const group = NAV_GROUPS.find((item) => item.items.some((nav) => nav.id === app.route));
    if (!group) return;
    setOpenGroups((current) => (current[group.id] ? current : { ...current, [group.id]: true }));
  }, [app.route]);
  useEffect(() => {
    try {
      localStorage.setItem("mediadesk.sidebar", collapsed ? "collapsed" : "expanded");
      localStorage.setItem("mediadesk.nav-groups", JSON.stringify(openGroups));
    } catch {
      /* private mode */
    }
  }, [collapsed, openGroups]);
  const workspace = topicsForAccount(app.state.topics, app.accountId);
  const counts = statusCounts(workspace);
  const liveTasks = tasksForAccount(app.state.tasks ?? [], app.accountId).filter((task) => task.status === "排队" || task.status === "进行中").length;
  const badges: Partial<Record<RouteId, { count: number; tip: string } | null>> = {
    today: itemBadge(
      decideTopics(workspace).length + reviewTopics(workspace).length + dueToday(workspace, todayInShanghai()).length,
      "",
    ),
    inbox: itemBadge(counts["待确认"], copy.badge.inbox(counts["待确认"])),
    kanban: itemBadge(counts["制作中"] + counts["待发布"], [counts["制作中"] ? copy.badge.board(counts["制作中"]) : "", counts["待发布"] ? copy.badge.publish(counts["待发布"]) : ""].filter(Boolean).join("，")),
    review: itemBadge(counts["待审阅"], copy.badge.review(counts["待审阅"])),
    settings: itemBadge(liveTasks, copy.badge.tasks(liveTasks)),
  };
  if (badges.today) badges.today = { ...badges.today, tip: copy.badge.today(badges.today.count) };
  const pending = app.state.outbox.length;
  const topic = app.state.topics.find((item) => item.id === app.selectedTopicId && !item.inTrash && topicAccount(item) === app.accountId) ?? null;
  const initial = (app.email.trim()[0] ?? "准").toUpperCase();
  const page = copy.pages[app.route];
  const hint = page.hint;
  const searchParams = new URLSearchParams(app.href.split("?")[1] ?? "");
  const showSearch = (app.route === "inbox" && searchParams.get("tab") !== "find") || app.route === "kanban";
  const kanbanView = readKanbanView(searchParams.get("view"));

  function go(route: RouteId) {
    app.navigate(route);
    setDrawerOpen(false);
  }

  return (
    <div className={collapsed ? "app-shell is-collapsed" : "app-shell"}>
      {drawerOpen ? (
        <button type="button" className="scrim" aria-label={copy.close} onClick={() => setDrawerOpen(false)} />
      ) : null}
      <aside className={drawerOpen ? "sidebar open" : "sidebar"}>
        <div className="logo">
          <span className="logo-mark" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </span>
          <b>{copy.brand}</b>
          <button
            type="button"
            className="sidebar-collapse"
            aria-pressed={collapsed}
            aria-label={collapsed ? copy.expandNav : copy.collapseNav}
            title={collapsed ? copy.expandNav : copy.collapseNav}
            onClick={() => setCollapsed((current) => !current)}
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>
        <nav aria-label="主导航">
          <NavButton item={HOME_ITEM} active={app.route === "today"} collapsed={collapsed} badge={badges.today ?? null} onClick={() => go("today")} />
          {NAV_GROUPS.map((group) => {
            const open = openGroups[group.id];
            const total = group.items.reduce((sum, item) => sum + (badges[item.id]?.count ?? 0), 0);
            const showItems = collapsed || open;
            return (
              <div key={group.id} className={open ? "nav-group open" : "nav-group"}>
                {collapsed ? null : (
                  <button
                    type="button"
                    className="group-toggle"
                    aria-expanded={open}
                    title={copy.nav.groups[group.id]}
                    onClick={() => setOpenGroups((current) => ({ ...current, [group.id]: !current[group.id] }))}
                  >
                    <span>{copy.nav.groups[group.id]}</span>
                    {!open && total > 0 ? (
                      <em className="nav-badge" title={copy.badge.group(total)}>{total}</em>
                    ) : null}
                    <ChevronDown size={14} />
                  </button>
                )}
                {showItems
                  ? group.items.map((item) => (
                      <NavButton
                        key={item.id}
                        item={item}
                        active={app.route === item.id}
                        collapsed={collapsed}
                        badge={badges[item.id] ?? null}
                        onClick={() => go(item.id)}
                      />
                    ))
                  : null}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-spacer" />
        <div className="sidebar-foot">
          <div className="sidebar-account">
            <span>{initial}</span>
            <div>
              <b>{app.email}</b>
              <small>{app.devPreview && !reviewShot() ? copy.localPreview : copy.signedIn}</small>
            </div>
          </div>
          {app.signOut ? (
            <button type="button" className="sidebar-feedback" title={copy.signOut} onClick={app.signOut}>
              <LogOut size={16} />
              <span>{copy.signOut}</span>
            </button>
          ) : null}
        </div>
      </aside>
      <main>
        <button type="button" className="menu-button" aria-label={copy.openNav} onClick={() => setDrawerOpen(true)}>
          <Menu size={18} />
        </button>
        <div className="page">
          <header className="app-header">
            <div className="header-copy">
              <h1>{page.title}</h1>
              {hint ? <p>{hint}</p> : null}
            </div>
            <div className="header-actions">
                <WorkspaceMenu />
                {showSearch ? (
                  <label className="search">
                    <Search size={16} />
                    <input
                      value={app.query}
                      onChange={(event) => app.setQuery(event.target.value)}
                      placeholder={copy.searchPlaceholder}
                      aria-label={copy.search}
                    />
                  </label>
                ) : null}
                {app.route === "kanban" ? (
                  <div className="view-switch" role="group" aria-label={copy.pages.kanban.title}>
                    {([
                      ["list", List, copy.lane.segments.list],
                      ["board", Columns3, copy.lane.segments.board],
                      ["calendar", Calendar, copy.lane.segments.calendar],
                    ] as const).map(([id, Icon, label]) => (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={kanbanView === id}
                        onClick={() => app.navigate("kanban", withKanbanView(app.href, id))}
                      >
                        <Icon size={14} strokeWidth={1.75} />
                        {label}
                      </button>
                    ))}
                  </div>
                ) : null}
                <SyncChip />
              <button type="button" className="button" onClick={() => void app.syncNow(true)} disabled={app.syncing}>
                <RefreshCw className={app.syncing ? "spinning" : undefined} />
                {copy.refresh}
              </button>
            </div>
          </header>
          {app.devPreview && !reviewShot() ? <div className="notice">{copy.devNotice}</div> : null}
          {!app.devPreview && !app.online ? <div className="notice warn">{copy.offline}</div> : null}
          {!app.devPreview && app.notion.lastError ? (
            <div className="notice danger">
              <AlertTriangle size={16} />
              {app.notion.lastError}
            </div>
          ) : null}
          {pending > 0 ? <div className="notice warn">{copy.pendingWrites(pending)}</div> : null}
          {app.authError ? (
            <div className="notice danger">
              <AlertTriangle size={16} />
              {app.authError}
            </div>
          ) : null}
          <ErrorBoundary variant="section" resetKey={app.route}>
            {app.route === "today" ? <Home /> : null}
            {app.route === "inbox" ? <Inbox /> : null}
            {app.route === "research" ? <Research /> : null}
            {app.route === "kanban" ? <KanbanDesk /> : null}
            {app.route === "review" ? <ReviewDesk /> : null}
            {app.route === "metrics" ? <Metrics /> : null}
            {app.route === "settings" ? <SettingsView /> : null}
          </ErrorBoundary>
        </div>
      </main>
      {app.addAccountOpen ? <AddAccount /> : null}
      {topic ? <DetailModal topic={topic} /> : null}
      <div className="toasts" aria-live="polite">
        {app.toasts.map((toast) => (
          <div key={toast.id} className={toast.tone === "danger" ? "toast danger" : "toast"}>
            <span>{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  const items = Array.isArray(toast.action!.undo) ? toast.action!.undo : [toast.action!.undo];
                  for (const item of items) app.setStatus(item.topicId, item.status);
                  app.dismissToast(toast.id);
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
            <button type="button" onClick={() => app.dismissToast(toast.id)} aria-label={copy.closeTip}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NavButton({
  item,
  active,
  collapsed,
  badge,
  onClick,
}: {
  item: { id: RouteId; icon: typeof HOME_ITEM.icon };
  active: boolean;
  collapsed: boolean;
  badge: { count: number; tip: string } | null;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <button type="button" className={active ? "nav-item active" : "nav-item"} title={navLabel(item.id)} onClick={onClick}>
      <Icon size={16} />
      <span>{navLabel(item.id)}</span>
      {badge ? (
        <em className="nav-badge" title={badge.tip}>
          {collapsed ? "" : badge.count}
        </em>
      ) : null}
    </button>
  );
}

function readKanbanView(value: string | null): "list" | "board" | "calendar" {
  if (value === "board" || value === "calendar") return value;
  return "list";
}

function withKanbanView(href: string, view: "list" | "board" | "calendar"): string {
  const params = new URLSearchParams(href.split("?")[1] ?? "");
  params.delete("calendar");
  params.set("view", view);
  return params.toString();
}

function readNavGroups(): Record<NavGroupId, boolean> {
  const fallback = { topics: true, make: true, ops: false };
  try {
    const raw = localStorage.getItem("mediadesk.nav-groups");
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Record<NavGroupId, boolean>>;
    return {
      topics: parsed.topics !== false,
      make: parsed.make !== false,
      ops: parsed.ops === true,
    };
  } catch {
    return fallback;
  }
}

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem("mediadesk.sidebar") === "collapsed";
  } catch {
    return false;
  }
}

function SyncChip() {
  const app = useApp();
  const error = app.notion.lastError;
  const label = app.devPreview
    ? copy.localPreview
    : !app.online
      ? copy.offline
      : error
        ? copy.syncError
        : app.syncing
          ? copy.syncing
          : app.notion.lastSyncAt
            ? copy.notionConnected
            : copy.waitingSync;
  const quiet = label === copy.offline ? "离线" : label;
  return (
    <span className={error && !app.devPreview ? "secondary danger-chip" : "secondary"} title={label}>
      {quiet}
    </span>
  );
}
